import { FC, useContext, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../../context/notificationContext";
import useI18n from "../../../../../../hooks/useTranslations";
import { InsertAdminRoofTypeBody } from "../../../../../../http/patrol/types/roofTypes";
import { useRoofTypesApi } from "../../../../../../http/patrol/useRoofTypes";
import Form from "../../../../../ui/Form";
import FormField from "../../../../../ui/FormField";
import FormFieldsContainer from "../../../../../ui/FormFieldsContainer";
import Input from "../../../../../ui/Input";
import Numeric from "../../../../../ui/Numeric";
import Switch from "../../../../../ui/Switch";
import VisibilityScope, { VisibilityScopeValue } from "../../../../../ui/VisibilityScope";

interface InsertRoofTypeForm {
  name: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  hasHips: boolean;
  canBeCustom: boolean;
  canBeMultiple: boolean;
}

const NewRoofType: FC = () => {
  const { systemId } = useParams<{ systemId: string }>();
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const roofTypesApi = useRoofTypesApi();

  const onSubmit = () => async (formData: InsertRoofTypeForm) => {
    const body: InsertAdminRoofTypeBody = {
      name: formData.name!,
      nameDe: null,
      nameEs: null,
      nameFr: null,
      nameIt: null,
      order: formData.order!,
      visibility: formData.visibility,
      hasHips: formData.hasHips,
      canBeCustom: formData.canBeCustom,
      canBeMultiple: formData.canBeMultiple,
      categoryId: categoryId!,
    };

    setIsSubmitting(true);

    try {
      const res = await roofTypesApi.insertAdminRoofType(body);
      navigate(`/admin/systems/${systemId}/roof_categories/${categoryId}/roof_types/${res.id}`);
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form<InsertRoofTypeForm>
      defaultValues={{
        name: null,
        order: 50,
        visibility: "hidden",
      }}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue, watch }) => {
        const hasHips = !!watch("hasHips");
        const canBeCustom = !!watch("canBeCustom");
        const canBeMultiple = !!watch("canBeMultiple");

        return (
          <FormFieldsContainer fullWidth>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isInvalid, isOptional }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isSubmitting}
                  {...register("name", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.hasHips} isOptional>
              {({ labelId }) => (
                <Switch id={labelId} checked={hasHips} onChange={(checked) => setValue("hasHips", checked)} />
              )}
            </FormField>
            <FormField label={i18n.translation.common.canBeCustom} isOptional>
              {({ labelId }) => (
                <Switch id={labelId} checked={canBeCustom} onChange={(checked) => setValue("canBeCustom", checked)} />
              )}
            </FormField>
            <FormField label={i18n.translation.common.canBeMultiple} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={canBeMultiple}
                  onChange={(checked) => setValue("canBeMultiple", checked)}
                />
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              disabled={isSubmitting}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default NewRoofType;
